import React, { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Pencil, Trash2, Check, X, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CategoryBadge, CAT_ICONS, CAT_COLORS } from "@/components/comms/CategoryBadge";

function LookPicker({ c, colors, icons, onLook }) {
  return (
    <Popover>
      <PopoverTrigger asChild><button type="button" className="rounded-lg hover:ring-2 hover:ring-slate-200 transition-shadow" data-testid={`cat-look-${c.id}`} aria-label="Change colour and icon"><CategoryBadge look={c} size="sm" /></button></PopoverTrigger>
      <PopoverContent className="bg-white w-64 p-3 space-y-3" data-testid={`cat-look-panel-${c.id}`}>
        <div><p className="text-xs font-semibold text-slate-600 mb-1.5">Colour</p>
          <div className="flex flex-wrap gap-1.5">{colors.map((k) => <button key={k} type="button" onClick={() => onLook(c, { color: k })} data-testid={`cat-color-${c.id}-${k}`}
            className={`w-6 h-6 rounded-full ${CAT_COLORS[k]} ${c.color === k ? "ring-2 ring-offset-1 ring-slate-500" : ""}`} aria-label={k} />)}</div></div>
        <div><p className="text-xs font-semibold text-slate-600 mb-1.5">Icon</p>
          <div className="grid grid-cols-8 gap-1">{icons.map((k) => { const Ic = CAT_ICONS[k]; return Ic ? <button key={k} type="button" onClick={() => onLook(c, { icon: k })} data-testid={`cat-icon-${c.id}-${k}`}
            className={`w-6 h-6 rounded flex items-center justify-center hover:bg-slate-100 ${c.icon === k ? "bg-slate-200" : ""}`} aria-label={k}><Ic className="w-3.5 h-3.5 text-slate-600" /></button> : null; })}</div></div>
      </PopoverContent>
    </Popover>
  );
}

function CatRow({ c, i, last, onMove, onRename, onDelete, onLook, colors, icons }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(c.name);
  const [desc, setDesc] = useState(c.description || "");
  const save = async () => { if (await onRename(c, name, desc)) setEditing(false); };
  return (
    <div className="flex items-center gap-2 py-2 border-b border-slate-100" data-testid={`cat-row-${c.id}`}>
      <div className="flex flex-col">
        <button type="button" disabled={i === 0} onClick={() => onMove(i, -1)} className="text-slate-400 disabled:opacity-30" data-testid={`cat-up-${c.id}`} aria-label="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
        <button type="button" disabled={last} onClick={() => onMove(i, 1)} className="text-slate-400 disabled:opacity-30" data-testid={`cat-down-${c.id}`} aria-label="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
      </div>
      <LookPicker c={c} colors={colors} icons={icons} onLook={onLook} />
      {editing ? (
        <>
          <div className="flex-1 space-y-1">
            <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); }} className="h-8" autoFocus data-testid={`cat-name-input-${c.id}`} />
            <Input value={desc} maxLength={160} onChange={(e) => setDesc(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); }} placeholder="Short description (optional)" className="h-8 text-xs" data-testid={`cat-desc-input-${c.id}`} />
          </div>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={save} data-testid={`cat-save-${c.id}`}><Check className="w-4 h-4 text-emerald-600" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditing(false); setName(c.name); setDesc(c.description || ""); }} data-testid={`cat-cancel-${c.id}`}><X className="w-4 h-4" /></Button>
        </>
      ) : (
        <>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-800" data-testid={`cat-name-${c.id}`}>{c.name}</p>
            {c.description && <p className="text-xs text-slate-500 truncate" data-testid={`cat-desc-${c.id}`}>{c.description}</p>}
          </div>
          <span className="text-xs text-slate-400">{c.template_count} template{c.template_count === 1 ? "" : "s"}</span>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(true)} data-testid={`cat-rename-${c.id}`} aria-label="Rename"><Pencil className="w-3.5 h-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" disabled={c.template_count > 0} title={c.template_count ? "Move its templates to another category first" : "Delete"}
            onClick={() => onDelete(c)} data-testid={`cat-delete-${c.id}`} aria-label="Delete"><Trash2 className="w-3.5 h-3.5 text-rose-500" /></Button>
        </>
      )}
    </div>
  );
}

export function ManageCategoriesDialog({ onClose, onChanged }) {
  const [cats, setCats] = useState([]);
  const [opts, setOpts] = useState({ colors: [], icons: [] });
  const [name, setName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const load = useCallback(() => api.get("/comm-template-categories").then((r) => { setCats(r.data.items); setOpts({ colors: r.data.colors || [], icons: r.data.icons || [] }); }).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); onChanged(); };
  const add = async () => {
    try { await api.post("/comm-template-categories", { name, description: newDesc }); setName(""); setNewDesc(""); toast.success("Category added"); changed(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const rename = async (c, n, d) => {
    try { const r = (await api.patch(`/comm-template-categories/${c.id}`, { name: n, description: d })).data; toast.success(`${n !== c.name ? "Renamed" : "Category updated"}${r.templates_moved ? ` · ${r.templates_moved} template${r.templates_moved === 1 ? "" : "s"} updated` : ""}`); changed(); return true; }
    catch (e) { toast.error(apiError(e)); return false; }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete the category "${c.name}"?`)) return;
    try { await api.delete(`/comm-template-categories/${c.id}`); toast.success("Category deleted"); changed(); } catch (e) { toast.error(apiError(e)); }
  };
  const look = async (c, v) => {
    setCats((cs) => cs.map((x) => x.id === c.id ? { ...x, ...v } : x));
    try { await api.patch(`/comm-template-categories/${c.id}`, v); onChanged(); } catch (e) { toast.error(apiError(e)); load(); }
  };
  const move = async (i, d) => {
    const next = [...cats]; [next[i], next[i + d]] = [next[i + d], next[i]]; setCats(next);
    try { await api.post("/comm-template-categories/reorder", { ids: next.map((c) => c.id) }); onChanged(); } catch (e) { toast.error(apiError(e)); load(); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white max-w-lg" data-testid="manage-categories-dialog">
        <DialogHeader><DialogTitle>Manage Categories</DialogTitle>
          <DialogDescription>Add, rename, reorder or restyle template categories for this account. Click a category's icon to change its colour and icon. Renaming updates every template in that category.</DialogDescription></DialogHeader>
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) add(); }} placeholder="New category name" data-testid="cat-new-input" />
          <Button onClick={add} disabled={!name.trim()} className="bg-brand text-white" data-testid="cat-add-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
        </div>
        <Input value={newDesc} maxLength={160} onChange={(e) => setNewDesc(e.target.value)} placeholder="Short description for the new category (optional)" className="-mt-2 text-xs" data-testid="cat-new-desc" />
        <div className="max-h-[50vh] overflow-y-auto" data-testid="cat-list">
          {cats.map((c, i) => <CatRow key={c.id + c.name} c={c} i={i} last={i === cats.length - 1} onMove={move} onRename={rename} onDelete={del} onLook={look} colors={opts.colors} icons={opts.icons} />)}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="cat-close">Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
